import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Divider, IconButton, Typography } from "@mzc-pdc/ui";
import { IconIndexing, IconCompleteCircle, IconErrorCircle, IconRetry } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import Tooltip from "@components_v2/Tooltip/tooltip";
import {
    ASSET_SEARCH_INDEX_ENGINE_LABEL,
    ASSET_SEARCH_INDEX_ERROR_CODE,
    ASSET_SEARCH_INDEX_IN_PROGRESS_STATUSES,
    ASSET_SEARCH_INDEX_STATUS,
} from "@constants";

/**
 * 원시 인덱싱 상태 문자열을 5종 표준값(COMPLETED/PARTIAL_COMPLETED/PENDING/FAILED/NOT_SUPPORTED)으로 정규화한다.
 * 서버의 진행 단계(DEFERRED/PENDING/SOURCE_SYNCING/EMBEDDING/INDEXING)는 PENDING 하나로 묶는다.
 * NOT_SUPPORTED(미지원)는 실패와 색상·아이콘은 같지만 라벨이 달라 별도 값으로 둔다.
 * 미지/누락 값은 진행 중(PENDING)으로 방어 처리한다.
 * @param {string} raw
 * @returns {"COMPLETED"|"PARTIAL_COMPLETED"|"PENDING"|"FAILED"|"NOT_SUPPORTED"}
 */
export const getAssetSearchIndexStatus = (raw) => {
    const normalized = String(raw ?? "")
        .trim()
        .toUpperCase();

    if (ASSET_SEARCH_INDEX_IN_PROGRESS_STATUSES.includes(normalized)) return ASSET_SEARCH_INDEX_STATUS.PENDING;

    switch (normalized) {
        case ASSET_SEARCH_INDEX_STATUS.COMPLETED:
            return ASSET_SEARCH_INDEX_STATUS.COMPLETED;
        case ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED:
            return ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED;
        case ASSET_SEARCH_INDEX_STATUS.FAILED:
            return ASSET_SEARCH_INDEX_STATUS.FAILED;
        case ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED:
            return ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED;
        default:
            return ASSET_SEARCH_INDEX_STATUS.PENDING;
    }
};

/**
 * 행 만들기·재시도 판정에 쓸 수 있는 엔진만 남긴다.
 *
 * ⚠️ `status` 없는 원소를 그냥 통과시키면 안 된다 — `getAssetSearchIndexStatus(undefined)` 가
 * 방어적으로 `PENDING` 을 돌려주므로 결측 원소 하나가 엔진 행을 「진행 중」으로 보이게 만든다.
 * 여기서 걸러 그 경로를 막는다. 전부 못 쓰면 빈 배열이 되고 호출부는 단일 `status` 로 돌아간다.
 *
 * 「매핑에 없는 엔진명은 원문 노출」과 혼동하지 말 것 — 그건 **이름**이 없는 경우이고
 * 여기는 **상태**가 없는 경우다. 상태 없는 엔진은 사용자에게 알릴 내용 자체가 없다. [DAM-573]
 */
const usableEngines = (engines) => (Array.isArray(engines) ? engines.filter((e) => e && e.status) : []);

/**
 * 인덱싱 재시도가 가능한 상태인지 판정한다.
 * 대상은 실패했거나 아직 인덱싱되지 않은(indexDoc 없음 → 최초 인덱싱) 자산이다.
 * 미지원 계열은 DAM-468 명세상 항상 status=NOT_SUPPORTED로 오므로(에러코드와 무관) 상태만으로 이미 제외된다.
 *
 * 부분 완료만 엔진 배열을 본다. 「완료+실패」와 「완료+미지원」이 종합 status 로는 둘 다
 * PARTIAL_COMPLETED 라 상태만으로 갈리지 않는데, 후자는 재인덱싱해도 결과가 같기 때문이다.
 * 엔진 단위 PARTIAL_COMPLETED 는 검색은 되지만 결함이 있고 조치는 재인덱싱으로 같으므로 성공으로 빼지 않는다.
 * 엔진 배열이 없으면(축 분리 전 응답) 결함이 있다는 사실은 status 가 이미 말했으므로 재시도를 막지 않는다. [DAM-604]
 * @param {{status?: string, engines?: Array<{status?: string}>}|null|undefined} indexDoc
 * @returns {boolean}
 */
export const isAssetSearchIndexRetryable = (indexDoc) => {
    const status = indexDoc?.status ? getAssetSearchIndexStatus(indexDoc.status) : null;
    if (status === null || status === ASSET_SEARCH_INDEX_STATUS.FAILED) return true;
    if (status !== ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED) return false;

    const defects = usableEngines(indexDoc.engines)
        .map((e) => getAssetSearchIndexStatus(e.status))
        .filter((s) => s !== ASSET_SEARCH_INDEX_STATUS.COMPLETED);
    return !defects.length || !defects.every((s) => s === ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED);
};

const STATUS_CONFIG = {
    [ASSET_SEARCH_INDEX_STATUS.COMPLETED]: {
        sx: {
            color: theme.palette.success.main,
            backgroundColor: theme.palette.success[50],
            borderColor: theme.palette.success[200],
        },
    },
    // 부분 완료는 pill 배경·테두리를 완료와 같은 success 로 둔다 — 검색되는 엔진이 살아 있다는 뜻이다.
    // 색을 완료와 맞추는 것은 status-badge.js 의 PARTIAL_COMPLETED 선례를 따른 것이다.
    // 문제가 함께 있다는 신호는 색이 아니라 상태 아이콘(빨강)이 진다. [DAM-573]
    [ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED]: {
        sx: {
            color: theme.palette.success.main,
            backgroundColor: theme.palette.success[50],
            borderColor: theme.palette.success[200],
        },
        statusIconColor: theme.palette.error.main,
    },
    [ASSET_SEARCH_INDEX_STATUS.PENDING]: {
        sx: {
            color: theme.palette.info.main,
            backgroundColor: theme.palette.info[50],
            borderColor: theme.palette.info[200],
        },
    },
    [ASSET_SEARCH_INDEX_STATUS.FAILED]: {
        sx: {
            color: theme.palette.error.main,
            backgroundColor: theme.palette.error[50],
            borderColor: theme.palette.error[200],
        },
    },
    // 미지원은 실패와 색상·아이콘을 공유하고 라벨과 사유 문구만 다르다.
    [ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED]: {
        sx: {
            color: theme.palette.error.main,
            backgroundColor: theme.palette.error[50],
            borderColor: theme.palette.error[200],
        },
    },
};

const SIZE_CONFIG = {
    sm: { iconSize: 12, sx: { width: 20, height: 20, justifyContent: "center" } },
    md: { iconSize: 12, sx: { height: 20, px: 0.5, gap: 0.25 } },
    lg: { iconSize: 16, sx: { height: 25, px: 1, gap: 0.75 } },
};

// 완료/진행중 툴팁 문구 키(common::msg::). 실패·미지원은 error.code 기반으로 별도 매핑한다.
const INFO_MESSAGE_KEY = {
    [ASSET_SEARCH_INDEX_STATUS.COMPLETED]: "You can find this asset in AI search.",
    [ASSET_SEARCH_INDEX_STATUS.PENDING]: "AI search indexing is in progress.",
    // 「나오지 않는다」로 단정하지 않는다 — 살아 있는 엔진으로는 찾힌다.
    // AI 검색 모드 안내 문구(DAM-574)와 어휘를 맞춘다 — 양쪽 다 "AI 검색 결과에 나온다/나오지 않는다"로 쓴다.
    [ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED]:
        "Some parts were not indexed, so this asset may not appear in all AI search results.",
};

// 실패·미지원 사유는 백엔드 메시지가 다양해 파악 불가 → error.code를 보고 프론트에서 문구를 매핑한다.
const ERROR_REASON_KEY = {
    // 미지원(status=NOT_SUPPORTED)
    [ASSET_SEARCH_INDEX_ERROR_CODE.UNSUPPORTED_MEDIA_TYPE]: "This is an unsupported media type.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.UNSUPPORTED_SOURCE_FORMAT]: "This is an unsupported source format.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.UNSUPPORTED_IMAGE_DIMENSION]:
        "This image is smaller than the minimum size (128x128 pixels).",
    [ASSET_SEARCH_INDEX_ERROR_CODE.UNSUPPORTED_IMAGE_SIZE]: "This image exceeds the maximum file size (5 MiB).",
    [ASSET_SEARCH_INDEX_ERROR_CODE.UNSUPPORTED_VIDEO_SIZE]: "This video exceeds the maximum file size (6 GB).",
    [ASSET_SEARCH_INDEX_ERROR_CODE.UNSUPPORTED_VIDEO_DURATION]: "This video exceeds the maximum duration (4 hours).",
    // 실패(재시도 가능)
    [ASSET_SEARCH_INDEX_ERROR_CODE.DISPATCH_PREPARE_FAILED]: "Failed to prepare indexing.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.METADATA_EXTRACT_FAILED]: "Failed to extract metadata.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.SOURCE_PREPARE_FAILED]: "Failed to create the source.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.SOURCE_SUPPORT_CHECK_FAILED]: "Failed to check source support.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.SOURCE_EMBED_FAILED]: "Failed to analyze the source.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.TITAN_EMBED_FAILED]: "Failed to analyze metadata.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.DOCUMENT_INDEX_FAILED]: "Failed to create the search index.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.INTERNAL_ERROR]: "An internal error occurred.",
};

// 부분 완료 엔진은 사유 코드가 실패와 겹치므로(SOURCE_EMBED_FAILED) 별도 매핑을 먼저 본다.
// 위 실패 문구를 그대로 쓰면 「실패했습니다」로 읽혀 일부는 성공했다는 사실이 지워진다. [DAM-604]
const PARTIAL_REASON_KEY = {
    [ASSET_SEARCH_INDEX_ERROR_CODE.SOURCE_EMBED_FAILED]: "Only part of the source was analyzed.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.PARTIAL_INDEXING_FAILED]: "Only some index entries were created.",
};

const DEFAULT_FAILURE_REASON_KEY = "AI search indexing failed.";
const DEFAULT_NOT_SUPPORTED_REASON_KEY = "AI search indexing is not supported for this asset.";

// 엔진별 행에서 쓰는 기본 사유. 위의 자산 단위 문구를 그대로 쓰면 안 된다 —
// 「이 자산은 지원하지 않습니다」는 엔진 하나만 빠진 부분 완료에서 거짓이다(다른 엔진으로는 찾힌다).
// 주어를 자산이 아니라 그 엔진의 분석으로 좁힌다. 엔진 이름은 바로 윗줄에 있으므로 여기서 반복하지 않는다. [DAM-573]
const DEFAULT_ENGINE_FAILURE_REASON_KEY = "This analysis failed.";
const DEFAULT_ENGINE_NOT_SUPPORTED_REASON_KEY = "This analysis is outside the supported range.";
const DEFAULT_ENGINE_PARTIAL_REASON_KEY = "This analysis was only partially indexed.";

// 문제 있는 엔진을 위로 정렬한다 — 사용자가 먼저 봐야 할 줄이 위에 온다.
const ENGINE_ROW_ORDER = {
    [ASSET_SEARCH_INDEX_STATUS.FAILED]: 0,
    [ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED]: 1,
    [ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED]: 2,
    [ASSET_SEARCH_INDEX_STATUS.PENDING]: 3,
    [ASSET_SEARCH_INDEX_STATUS.COMPLETED]: 4,
};

/**
 * 엔진 배열을 화면에 그릴 행으로 바꾼다. 배지 툴팁과 개요 패널 표가 같은 함수를 쓴다 —
 * 같은 내용을 두 곳에서 따로 만들면 정렬이나 표시명이 갈린다.
 *
 * 이름을 못 만든 행은 버린다 — `t()` 에 `undefined` 가 들어가면 키 문자열이 그대로 화면에 노출된다.
 * 재시도 판정은 `usableEngines` 를 직접 쓰므로 그 엔진의 상태는 여전히 셈에 들어간다(이름은 없어도 상태는 안다).
 * @param {Array} engines
 */
const buildEngineRows = (engines) =>
    usableEngines(engines)
        .map((e) => ({
            engineType: e.engineType,
            // 표시명은 프론트 매핑 → engineType 원문 순으로 떨어진다.
            // 원문까지 떨어뜨리는 이유는 매핑에 없는 엔진을 숨기면 합이 맞지 않아서다(명세도 원문 표시를 요구한다).
            label: ASSET_SEARCH_INDEX_ENGINE_LABEL[e.engineType] ?? e.engineType,
            status: getAssetSearchIndexStatus(e.status),
            errorCode: e.error?.code,
        }))
        .filter((row) => row.label)
        .sort((a, b) => (ENGINE_ROW_ORDER[a.status] ?? 9) - (ENGINE_ROW_ORDER[b.status] ?? 9));

const isEngineRowProblem = (row) =>
    row.status === ASSET_SEARCH_INDEX_STATUS.FAILED ||
    row.status === ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED ||
    row.status === ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED;

// 엔진 행의 사유 문구 키. 구체 코드가 있으면 그 문구를, 없으면 상태별 기본 문구를 쓴다.
const getEngineReasonKey = (row) => {
    if (row.status === ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED) {
        return PARTIAL_REASON_KEY[row.errorCode] ?? DEFAULT_ENGINE_PARTIAL_REASON_KEY;
    }
    return (
        ERROR_REASON_KEY[row.errorCode] ??
        (row.status === ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED
            ? DEFAULT_ENGINE_NOT_SUPPORTED_REASON_KEY
            : DEFAULT_ENGINE_FAILURE_REASON_KEY)
    );
};

/**
 * 배지 툴팁 안의 엔진별 상태 목록 — **어느 엔진이 빠졌나는 여기서만 말한다.**
 *
 * 자산 상세 개요 패널은 프록시 항목처럼 **배지 하나**(라벨 포함)만 둔다. 상단 헤더에서 이미
 * 같은 상태를 보여주고 있으므로 개요까지 엔진 내역을 펴면 같은 말을 세 번 하게 된다.
 * 한때 개요가 이 렌더러를 그대로 썼는데, 그 바람에 사유 문장까지 개요로 딸려 들어가
 * **개요가 실패 상세를 설명하는 자리처럼 보였다**(FE 혼선 실측).
 *
 * 형태 결정 —
 *   · 칩은 쓰지 않는다. 툴팁 머리의 요약 상태에만 칩을 두고 엔진 행은 텍스트로 눌러 위계를 하나로 만든다.
 *     엔진마다 칩을 달았더니 요약과 상세가 같은 무게가 되어 「어디를 봐야 하는지」가 사라졌다.
 *   · 엔진 이름은 왼쪽, 상태 낱말은 오른쪽 끝. 엔진이 늘어도 상태가 한 열에 모여 세로로 훑힌다.
 *   · 점이 상태 색을 지지만 낱말이 함께 있으므로 색이 유일한 단서가 아니다.
 *   · 정상인 엔진은 흐리게 둔다. 합을 맞추려면 보여야 하지만 시선을 끌 이유는 없다. [DAM-573]
 */
const EngineRowList = ({ rows }) => {
    const { t } = useTranslation();
    const nameColor = theme.palette.common.white;
    const reasonColor = theme.palette.greySecondary[200];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, width: "100%" }}>
            {rows.map((row) => {
                const hasReason = isEngineRowProblem(row);
                const dim = !hasReason && row.status !== ASSET_SEARCH_INDEX_STATUS.PENDING;
                return (
                    <Box
                        key={row.engineType ?? row.label}
                        sx={{ display: "flex", flexDirection: "column", gap: 0.25, opacity: dim ? 0.6 : 1 }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box
                                component={"span"}
                                aria-hidden
                                sx={{
                                    flexShrink: 0,
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    backgroundColor: STATUS_CONFIG[row.status].sx.color,
                                }}
                            />
                            <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: nameColor, flex: 1 }}>
                                {t(`common::label::${row.label}`, row.label)}
                            </Typography>
                            <Typography
                                sx={{
                                    flexShrink: 0,
                                    fontSize: 12,
                                    lineHeight: 1.5,
                                    whiteSpace: "nowrap",
                                    color: nameColor,
                                }}
                            >
                                {t(`common::label::${row.status}`, row.status)}
                            </Typography>
                        </Box>
                        {/* 사유는 점 너비만큼 들여써서 어느 엔진의 사유인지 보이게 한다. */}
                        {hasReason && (
                            <Typography sx={{ pl: 1.5, fontSize: 11, lineHeight: 1.6, color: reasonColor }}>
                                {t(`common::msg::${getEngineReasonKey(row)}`, getEngineReasonKey(row))}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

EngineRowList.propTypes = {
    rows: PropTypes.array.isRequired,
};

/**
 * 자산의 AI 검색모드 인덱싱 상태를 아이콘 pill로 표시하는 공통 컴포넌트.
 * 호버 시 상태별 리치 툴팁(타이틀 + 상태 칩 / 구분선 / 엔진별 상태 또는 상태별 사유)을 항상 제공한다.
 * showLabel이 true면 pill 우측에 상태 텍스트를 함께 노출하고, 텍스트 호버에도 동일한 툴팁이 뜬다(목록 테이블 뷰용).
 *
 * @param {object} props
 * @param {string} props.status - 원시 인덱싱 상태 문자열(대소문자 무관). 서버가 계산한 종합 상태이며 내부에서 정규화.
 * @param {"sm"|"md"|"lg"} [props.size="md"] - 픽셀 크기 + 우측 상태 아이콘 노출(sm=미노출, md·lg=노출).
 * @param {boolean} [props.showLabel=false] - pill 우측에 상태 텍스트 라벨 노출(재시도 버튼과 병용 불가).
 * @param {string} [props.errorCode] - 엔진 배열이 없을 때 사유 문구를 결정하는 사유 코드. 미지정 시 상태별 기본 문구.
 * @param {Array} [props.engines] - 엔진(MARENGO/METADATA)별 상태. 툴팁 엔진 행과 재시도 노출 판정에 쓴다.
 * @param {function} [props.onRetry] - 전달 시 재시도 가능 상태에서 배지 우측에 재시도 버튼 + 툴팁 안내 노출(상세 전용).
 * @param {boolean} [props.retryDisabled=false] - 재시도 버튼 비활성화(예: 재요청 진행 중 중복 차단).
 * @param {object} [props.sx] - 추가 스타일.
 */
const AssetSearchIndexStatus = ({
    status,
    size = "md",
    showLabel = false,
    errorCode,
    engines,
    onRetry,
    retryDisabled = false,
    sx,
}) => {
    const { t } = useTranslation();

    // 종합 상태는 서버가 engines 를 합쳐 내려준 status 다. 프론트에서 다시 접지 않는다 —
    // 고급검색 필터가 서버의 종합 status 로 거르므로, 따로 접으면 필터 결과와 배지가 갈린다. [DAM-604]
    const normalizedStatus = getAssetSearchIndexStatus(status);
    const statusConfig = STATUS_CONFIG[normalizedStatus];
    const sizeConfig = SIZE_CONFIG[size] ?? SIZE_CONFIG.md;
    const { iconSize } = sizeConfig;
    const showStatusIcon = sizeConfig !== SIZE_CONFIG.sm;

    const isFailed = normalizedStatus === ASSET_SEARCH_INDEX_STATUS.FAILED;
    const isNotSupported = normalizedStatus === ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED;
    const isPartial = normalizedStatus === ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED;

    // 엔진별 행. 문제 있는 엔진이 위로 온다. 개요 패널 표와 같은 함수를 쓴다.
    const engineRows = buildEngineRows(engines);

    // 재시도 노출 판정은 행 메뉴·일괄 바와 같은 함수를 쓴다(단일 소스) — 갈리면 배지 버튼 유무와
    // 메뉴 활성 여부가 어긋난다. `status` 를 함께 보는 이유는 헬퍼가 빈 status 를 「indexDoc 자체가
    // 없음 = 최초 인덱싱」으로 읽기 때문이다. 배지에서는 status 가 required 라 그 뜻이 성립하지 않는다.
    const showRetry = !!status && isAssetSearchIndexRetryable({ status, engines }) && typeof onRetry === "function";

    const searchModeLabel = t("common::label::AI search mode", "AI search mode");
    const statusLabel = t(`common::label::${normalizedStatus}`, normalizedStatus);
    // 엔진 배열이 없는 응답에서만 쓰는 자산 단위 사유. 실패·미지원은 [사유 라벨 + 사유 문구] 2단으로 편다.
    const hasReason = isFailed || isNotSupported;
    const reasonKey =
        ERROR_REASON_KEY[errorCode] ?? (isNotSupported ? DEFAULT_NOT_SUPPORTED_REASON_KEY : DEFAULT_FAILURE_REASON_KEY);
    const reason = t(`common::msg::${reasonKey}`, reasonKey);
    const reasonLabel = isNotSupported
        ? t("common::label::Unsupported reason", "Unsupported reason")
        : t("common::label::Failure reason", "Failure reason");
    const infoMessageKey = INFO_MESSAGE_KEY[normalizedStatus];
    const infoMessage = infoMessageKey ? t(`common::msg::${infoMessageKey}`, infoMessageKey) : "";
    // 실패/미지원 상태에서는 사유까지 읽어주도록 aria-label에 포함(리치 툴팁은 시각 전용).
    //
    // 엔진이 오면 엔진별 상태·사유도 함께 읽어준다. 툴팁이 시각 전용이라 여기 안 넣으면
    // 스크린리더 사용자는 「부분 완료」까지만 듣고 **어느 엔진이 빠졌는지 알 수 없다**.
    // 개요 패널 표는 실제 DOM 텍스트라 읽히지만, 카드·목록의 배지에는 그 대체 수단이 없다. [DAM-573]
    const engineAriaText = engineRows
        .map((row) => {
            const name = t(`common::label::${row.label}`, row.label);
            const state = t(`common::label::${row.status}`, row.status);
            // 이름과 상태를 공백으로만 이으면 낭독이 한 덩어리가 된다 —
            // 「영상·이미지 내용 분석 미지원」이 「분석 미지원」이라는 한 구절로 들린다.
            if (!isEngineRowProblem(row)) return `${name}: ${state}`;
            const rowReason = t(`common::msg::${getEngineReasonKey(row)}`, getEngineReasonKey(row));
            return `${name}: ${state}. ${rowReason}`;
        })
        .join(" ");
    // 축과 축, 상태와 사유 사이를 마침표로 끊는다. 스크린리더는 마침표에서 쉬므로
    // 「AI 검색 모드 부분 완료 영상…」처럼 앞말과 뒷말이 붙어 들리는 것을 막는다.
    // 툴팁은 시각적으로 이미 줄이 나뉘어 있는데 낭독용 문자열만 뭉쳐 있었다.
    const ariaLabel = [searchModeLabel, statusLabel, engineAriaText || (hasReason ? reason : "")]
        .filter(Boolean)
        .join(". ");

    const handleRetryClick = (e) => {
        e.stopPropagation();
        if (retryDisabled) return;
        onRetry();
    };

    const renderStatusIcon = () => {
        switch (normalizedStatus) {
            case ASSET_SEARCH_INDEX_STATUS.COMPLETED:
                return <IconCompleteCircle size={iconSize} color={"inherit"} />;
            // 부분 완료만 색을 inherit 하지 않는다 — pill 은 success 인데 아이콘은 error 여야 하기 때문이다.
            //
            // 아이콘은 에러 원을 쓴다. 삼각형을 검토했다가 되돌렸다 —
            // 이 저장소는 삼각형=warning(주황) · 원=error(빨강)로 짝이 고정돼 있고
            // (autocomplete-multiple-projects.jsx:220-221 이 두 갈래를 나란히 쓴다),
            // 부분 실패의 색은 error 다(job-item.jsx:131 의 `job.isPartialFailure ? "error.main"`).
            // 빨간 삼각형은 이 저장소에 없는 조합이라 어휘를 깬다. [DAM-573]
            case ASSET_SEARCH_INDEX_STATUS.PARTIAL_COMPLETED:
                return <IconErrorCircle size={iconSize} htmlColor={statusConfig.statusIconColor} />;
            case ASSET_SEARCH_INDEX_STATUS.FAILED:
            case ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED:
                return <IconErrorCircle size={iconSize} color={"inherit"} />;
            case ASSET_SEARCH_INDEX_STATUS.PENDING:
            default:
                return <CircularProgress size={iconSize} color={"inherit"} />;
        }
    };

    // sm(20×20 원형)은 상태 아이콘을 그릴 자리가 없어 색만으로 상태를 말한다.
    // 그런데 부분 완료는 pill 색이 완료와 같은 success 라, sm 에서 둘이 완전히 똑같아진다.
    // 카드 배지 슬롯은 DRM·AI·잠금·인덱싱 4칸(86px)으로 고정돼 있어 옆에 아이콘을 붙일 수도 없다
    // (AssetCard.js 주석). 그래서 pill 안 우하단에 점을 겹쳐 슬롯을 늘리지 않고 가른다. [DAM-573]
    const renderPartialDot = () => (
        <Box
            component={"span"}
            aria-hidden
            sx={{
                position: "absolute",
                right: -1,
                bottom: -1,
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: statusConfig.statusIconColor,
                // pill 테두리·이웃 배지와 붙어 보이지 않도록 흰 링으로 떼어낸다.
                border: `1.5px solid ${theme.palette.common.white}`,
                boxSizing: "border-box",
            }}
        />
    );

    const retryHint = (
        <>
            <Divider sx={{ borderColor: theme.palette.greySecondary[500] }} />
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, color: theme.palette.greySecondary[200] }}>
                <IconRetry size={16} color={"inherit"} />
                <Typography sx={{ fontSize: 11, color: theme.palette.greySecondary[200] }}>
                    {t(
                        "common::msg::Click the button on the badge to retry.",
                        "Click the button on the badge to retry.",
                    )}
                </Typography>
            </Box>
        </>
    );

    const tooltipContent = (
        // 로케일별 문구 길이를 흡수하도록 고정 폭 대신 minWidth~maxWidth 구간을 쓴다.
        // 상한을 두지 않으면 본문 사유 문구가 한 줄로 길게 늘어난다.
        <Box sx={{ minWidth: 180, maxWidth: 260, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* 상태 chip은 잘리면 안 되므로(flexShrink 0 + nowrap) 넘치는 길이는 이 라벨이 줄바꿈으로 흡수한다. */}
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: theme.palette.common.white }}>
                    {searchModeLabel}
                </Typography>
                <Box
                    component={"span"}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        flexShrink: 0,
                        px: 0.5,
                        borderRadius: "100px",
                        backgroundColor: statusConfig.sx.color,
                        color: theme.palette.common.white,
                        fontSize: 12,
                        lineHeight: 1.5,
                        whiteSpace: "nowrap",
                    }}
                >
                    {statusLabel}
                </Box>
            </Box>
            <Divider sx={{ borderColor: theme.palette.greySecondary[500] }} />
            {engineRows.length ? (
                <EngineRowList rows={engineRows} />
            ) : hasReason ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Typography sx={{ fontSize: 11, color: theme.palette.common.white, opacity: 0.8 }}>
                        {reasonLabel}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: theme.palette.common.white }}>{reason}</Typography>
                </Box>
            ) : (
                <Typography sx={{ fontSize: 11, color: theme.palette.greySecondary[200] }}>{infoMessage}</Typography>
            )}
            {showRetry && !retryDisabled && retryHint}
        </Box>
    );

    const retryLabel = t("common::label::Retry AI search indexing", "Retry AI search indexing");

    // 상태 리치 툴팁 부착 헬퍼. 재시도 캡슐에서는 badge 세그먼트에만 감싸 재시도 버튼 툴팁과 중첩되지 않게 한다.
    const withStatusTooltip = (node) => {
        return (
            <Tooltip
                title={tooltipContent}
                placement={"bottom"}
                componentsProps={{
                    tooltip: {
                        sx: {
                            // 디자인: 컨테이너가 콘텐츠를 px 8 / py 4로 감싼다.
                            py: 0.5,
                            px: 1,
                            backgroundColor: theme.palette.greySecondary[600],
                        },
                    },
                }}
            >
                {node}
            </Tooltip>
        );
    };

    // 단일 pill — 카드/목록 등 재시도 버튼이 없는 모든 경우.
    if (!showRetry) {
        const pill = (
            <Box
                role={"img"}
                aria-label={ariaLabel}
                // 이 배지의 툴팁이 「어느 축이 빠졌는지」를 말하는 유일한 자리다.
                // tabIndex 가 없으면 마우스를 못 쓰는 사용자는 그 정보에 닿을 방법이 없다 —
                // 특히 카드 뷰에는 「부분 완료」 글자조차 없어 8px 점이 전부다.
                // 같은 구조의 components_v2/analysis-source-notice 가 이미 이렇게 한다. [DAM-586]
                tabIndex={0}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    borderRadius: "100px",
                    border: 1,
                    // 점을 pill 모서리에 얹기 위한 기준. 점이 테두리 밖으로 1px 나가므로 자르지 않는다.
                    // 점을 그리는 부분 완료에서만 건다 — 항상 걸면 배지가 위치 지정 요소가 되어
                    // 엔진이 안 오는 기존 자산의 쌓임 순서까지 바뀐다.
                    ...(isPartial && { position: "relative" }),
                    ...statusConfig.sx,
                    ...sizeConfig.sx,
                    ...(!showLabel && sx),
                }}
            >
                <IconIndexing size={iconSize} color={"inherit"} />
                {showStatusIcon && renderStatusIcon()}
                {/* 상태 아이콘이 없는 sm 에서만 점으로 가른다. 아이콘이 있으면 그 아이콘이 이미 빨강이라 중복이다. */}
                {!showStatusIcon && isPartial && renderPartialDot()}
            </Box>
        );

        // 라벨 포함 시 pill + 텍스트를 하나의 트리거로 묶어 텍스트 호버에도 툴팁이 뜨게 한다.
        return withStatusTooltip(
            showLabel ? (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, ...sx }}>
                    {pill}
                    <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: statusConfig.sx.color }}>
                        {statusLabel}
                    </Typography>
                </Box>
            ) : (
                pill
            ),
        );
    }

    // [상태 배지 + 재시도 버튼] 2세그먼트 캡슐(상세 전용).
    // 캡슐 테두리·높이는 컨테이너가 소유하고 세그먼트 구분선은 badge의 우측 테두리 —
    // 두 세그먼트가 stretch로 같은 높이를 갖게 해 구분선 틀어짐을 방지한다.
    return (
        <Box
            role={"group"}
            aria-label={ariaLabel}
            sx={{
                display: "inline-flex",
                alignItems: "stretch",
                height: 24,
                boxSizing: "border-box",
                border: 1,
                borderColor: statusConfig.sx.borderColor,
                borderRadius: "100px",
                overflow: "hidden",
                ...sx,
            }}
        >
            {withStatusTooltip(
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1,
                        color: statusConfig.sx.color,
                        backgroundColor: statusConfig.sx.backgroundColor,
                        borderRight: 1,
                        borderColor: statusConfig.sx.borderColor,
                    }}
                >
                    <IconIndexing size={iconSize} color={"inherit"} />
                    {renderStatusIcon()}
                </Box>,
            )}
            <Tooltip title={retryLabel} placement={"top"}>
                <Box sx={{ display: "inline-flex", alignItems: "center", pr: 0.5 }}>
                    <IconButton
                        circled
                        size={"small"}
                        aria-label={retryLabel}
                        aria-disabled={retryDisabled}
                        onClick={handleRetryClick}
                        sx={{
                            width: 22,
                            height: 22,
                            p: 0,
                            ...(retryDisabled && { opacity: 0.5, pointerEvents: "none" }),
                        }}
                    >
                        <IconRetry size={16} />
                    </IconButton>
                </Box>
            </Tooltip>
        </Box>
    );
};

const enginesPropType = PropTypes.arrayOf(
    PropTypes.shape({
        engineType: PropTypes.string,
        status: PropTypes.string,
        error: PropTypes.shape({ code: PropTypes.string }),
    }),
);

AssetSearchIndexStatus.propTypes = {
    status: PropTypes.string.isRequired,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    showLabel: PropTypes.bool,
    errorCode: PropTypes.string,
    engines: enginesPropType,
    onRetry: PropTypes.func,
    retryDisabled: PropTypes.bool,
    sx: PropTypes.object,
};

export default AssetSearchIndexStatus;
